'use client';
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

export interface CartItem {
  id: string; // cart_items row id
  listingId: string;
  title: string;
  price: number;
  qty: number;
  image: string;
  condition: string;
  seller: string;
  sellerId: string;
  paywiseUrl?: string;
  paypalUrl?: string;
  maxQty: number;
  shippingCost: number;
}

interface CartContextValue {
  cartItems: CartItem[];
  cartCount: number;
  subtotal: number;
  shippingTotal: number;
  loading: boolean;
  addToCart: (listing: {
    id: string;
    title: string;
    price: number;
    images: string[];
    condition: string;
    seller?: string;
    seller_id?: string;
    sellerId?: string;
    quantity: number;
    seller_profile?: { username?: string; full_name?: string; paywise_url?: string; paypal_url?: string };
  }) => Promise<void>;
  removeFromCart: (listingId: string) => Promise<void>;
  updateQty: (listingId: string, newQty: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const supabase = createClient();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!user) { setCartItems([]); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cart_items')
        .select(`
          id,
          listing_id,
          quantity,
          listing:listing_id(
            id, title, price, condition, images, quantity, seller_id, shipping_cost,
            seller_profile:seller_id(full_name, username, paywise_url, paypal_url)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const dbItems: CartItem[] = (data || []).map((row: any) => ({
        id: row.id,
        listingId: row.listing_id,
        title: row.listing?.title || '',
        price: Number(row.listing?.price) || 0,
        qty: row.quantity,
        image: row.listing?.images?.[0] || '',
        condition: row.listing?.condition || '',
        seller: row.listing?.seller_profile?.username || row.listing?.seller_profile?.full_name || '',
        sellerId: row.listing?.seller_id || '',
        paywiseUrl: row.listing?.seller_profile?.paywise_url || '',
        paypalUrl: row.listing?.seller_profile?.paypal_url || '',
        maxQty: row.listing?.quantity || 1,
        shippingCost: Number(row.listing?.shipping_cost) || 0,
      }));

      setCartItems(dbItems);
    } catch {
      // silent fail on fetch
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = useCallback(async (listing: {
    id: string;
    title: string;
    price: number;
    images: string[];
    condition: string;
    seller?: string;
    seller_id?: string;
    sellerId?: string;
    quantity: number;
    seller_profile?: { username?: string; full_name?: string; paywise_url?: string };
  }) => {
    if (!user) {
      toast.error('Please sign in to add items to your cart');
      return;
    }
    if (listing.quantity === 0) {
      toast.error('This listing is sold out');
      return;
    }

    try {
      // Check if already in cart
      const { data: existing } = await supabase
        .from('cart_items')
        .select('id, quantity')
        .eq('user_id', user.id)
        .eq('listing_id', listing.id)
        .maybeSingle();

      if (existing) {
        const newQty = Math.min(existing.quantity + 1, listing.quantity);
        const { error } = await supabase
          .from('cart_items')
          .update({ quantity: newQty })
          .eq('id', existing.id);
        if (error) throw error;
        toast.success(`Updated quantity in cart`);
      } else {
        const { error } = await supabase
          .from('cart_items')
          .insert({ user_id: user.id, listing_id: listing.id, quantity: 1 });
        if (error) throw error;
        toast.success(`${listing.title} added to cart`);
      }

      await fetchCart();
    } catch (err: any) {
      toast.error(err.message || 'Failed to add to cart');
    }
  }, [user, fetchCart]);

  const removeFromCart = useCallback(async (listingId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', user.id)
        .eq('listing_id', listingId);
      if (error) throw error;
      setCartItems(prev => prev.filter(i => i.listingId !== listingId));
      toast.info('Item removed from cart');
    } catch (err: any) {
      toast.error('Failed to remove item');
    }
  }, [user]);

  const updateQty = useCallback(async (listingId: string, newQty: number) => {
    if (!user) return;
    const item = cartItems.find(i => i.listingId === listingId);
    if (!item) return;
    if (newQty < 1 || newQty > item.maxQty) return;

    try {
      const { error } = await supabase
        .from('cart_items')
        .update({ quantity: newQty })
        .eq('user_id', user.id)
        .eq('listing_id', listingId);
      if (error) throw error;
      setCartItems(prev => prev.map(i => i.listingId === listingId ? { ...i, qty: newQty } : i));
    } catch (err: any) {
      toast.error('Failed to update quantity');
    }
  }, [user, cartItems]);

  const clearCart = useCallback(async () => {
    if (!user) return;
    try {
      await supabase.from('cart_items').delete().eq('user_id', user.id);
      setCartItems([]);
    } catch {}
  }, [user]);

  const cartCount = cartItems.reduce((s, i) => s + i.qty, 0);
  const subtotal = cartItems.reduce((s, i) => s + i.price * i.qty, 0);

  // Merged shipping: one charge per seller (max shipping_cost among their items)
  const shippingTotal = (() => {
    const sellerShipping: Record<string, number> = {};
    cartItems.forEach(item => {
      if (!sellerShipping[item.sellerId] || item.shippingCost > sellerShipping[item.sellerId]) {
        sellerShipping[item.sellerId] = item.shippingCost;
      }
    });
    return Object.values(sellerShipping).reduce((s, v) => s + v, 0);
  })();

  return (
    <CartContext.Provider value={{
      cartItems,
      cartCount,
      subtotal,
      shippingTotal,
      loading,
      addToCart,
      removeFromCart,
      updateQty,
      clearCart,
      refreshCart: fetchCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
